<template>
  <form @submit.prevent="save" autocomplete="off" class="relative">
    <div
        v-show="busy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-20"
    >
      <spinner :color="'text-black'" :size="10"/>
    </div>

    <div class="w-full bg-white p-5 rounded">
      <div class="mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Customer
        </h3>
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">
          Customer
          <span class="text-red-500">*</span>
        </label>
        <div @focusin="customerFocused = true" @focusout="customerFocused = false" data-testid="customer-field">
          <ContactAutocomplete
              class="w-full"
              v-model="selectedContact"
              :is_customer="true"
              :disabled="isContactLocked"
              @contactSelected="handleContactSelected"
              @contactCleared="handleContactCleared"
          />
        </div>
        <p v-if="validationErrors.contact_id" class="text-red-600 text-sm mt-1">
          {{ validationErrors.contact_id }}
        </p>
        <p v-else-if="isTypedCustomer && !customerFocused" class="text-gray-600 text-sm mt-1" data-testid="typed-customer-note">
          No contact record linked. Equipment, addresses and emails will not be filled in for this customer.
        </p>
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">Equipment</label>
        <Dropdown
            class="w-full bge-input rounded service-ticket-control"
            v-model="ticket.equipment_id"
            :options="customersEquipment"
            optionLabel="label"
            optionValue="equipment_id"
            :showClear="true"
            :disabled="customersEquipment.length === 0"
            placeholder="Select equipment"
        />
      </div>

      <div class="w-full mb-4 lg:flex lg:space-x-4">
        <div class="w-full lg:w-1/2">
          <label class="uppercase font-semibold text-sm">
            Contact Name (Additional)
            <span class="text-red-500">*</span>
          </label>
          <input
              v-model="ticket.contact_name_additional"
              class="bge-input bge-input-spacing w-full rounded"
              type="text"
          />
          <p v-if="validationErrors.contact_name_additional" class="text-red-600 text-sm mt-1">
            {{ validationErrors.contact_name_additional }}
          </p>
        </div>
        <div class="w-full lg:w-1/2 mt-4 lg:mt-0">
          <label class="uppercase font-semibold text-sm">
            Contact Number (Additional)
            <span class="text-red-500">*</span>
          </label>
          <input
              v-model="ticket.contact_number_additional"
              class="bge-input bge-input-spacing w-full rounded"
              type="text"
          />
          <p v-if="validationErrors.contact_number_additional" class="text-red-600 text-sm mt-1">
            {{ validationErrors.contact_number_additional }}
          </p>
        </div>
      </div>
    </div>

    <div class="w-full bg-white p-5 rounded mt-6">
      <div class="mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Ticket Details
        </h3>
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">
          Ticket Title
          <span class="text-red-500">*</span>
        </label>
        <input
            v-model="ticket.title"
            class="bge-input bge-input-spacing w-full rounded"
            type="text"
        />
        <p v-if="validationErrors.title" class="text-red-600 text-sm mt-1">
          {{ validationErrors.title }}
        </p>
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">Severity</label>
        <Dropdown
            class="w-full bge-input rounded service-ticket-control"
            v-model="ticket.severity"
            :options="severityOptions"
            optionLabel="label"
            optionValue="value"
        />
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">Description of Issue</label>
        <Textarea
            v-model="ticket.description"
            class="bge-input bge-input-spacing w-full rounded"
            :autoResize="false"
            rows="5"
        />
      </div>

      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">Follow Up At</label>
        <Calendar
            class="w-full service-ticket-calendar"
            v-model="ticket.follow_up_at"
            dateFormat="dd/mm/yy"
            :showIcon="true"
        />
      </div>
    </div>

    <div class="w-full bg-white p-5 rounded mt-6">
      <div class="mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Warranty &amp; Returns
        </h3>
      </div>

      <div class="w-full mb-4 lg:flex lg:space-x-4">
        <div class="w-full lg:w-1/2">
          <label class="block uppercase font-semibold text-sm mb-1">Warranty</label>
          <InputSwitch
              v-model="ticket.is_warranty"
              :disabled="isWarrantyLocked"
              data-testid="warranty-switch"
          />
        </div>
        <div class="w-full lg:w-1/2 mt-4 lg:mt-0">
          <label class="block uppercase font-semibold text-sm mb-1">Items for Return</label>
          <InputSwitch
              v-model="ticket.items_for_return"
              data-testid="items-for-return-switch"
          />
        </div>
      </div>

      <div class="w-full mb-4" v-if="ticket.is_warranty">
        <label class="uppercase font-semibold text-sm">
          Reason Warranty Agreed
          <span v-if="!isWarrantyLocked" class="text-red-500">*</span>
        </label>
        <Textarea
            v-model="ticket.warranty_reason"
            class="bge-input bge-input-spacing w-full rounded"
            :autoResize="false"
            :disabled="isWarrantyLocked"
            rows="3"
            data-testid="warranty-reason"
        />
        <p v-if="validationErrors.warranty_reason" class="text-red-600 text-sm mt-1">
          {{ validationErrors.warranty_reason }}
        </p>
      </div>

      <div class="w-full mb-4" v-if="ticket.items_for_return">
        <label class="uppercase font-semibold text-sm">
          Items for Return Details
          <span class="text-red-500">*</span>
        </label>
        <Textarea
            v-model="ticket.items_for_return_notes"
            class="bge-input bge-input-spacing w-full rounded"
            :autoResize="false"
            rows="3"
            data-testid="items-for-return-notes"
        />
        <p v-if="validationErrors.items_for_return_notes" class="text-red-600 text-sm mt-1">
          {{ validationErrors.items_for_return_notes }}
        </p>
      </div>

      <div class="w-full">
        <label class="uppercase font-semibold text-sm">Created by User</label>
        <input
            :value="createdByUser"
            readonly
            class="bge-input bge-input-spacing w-full rounded text-gray-500"
            type="text"
        />
      </div>
    </div>

    <div class="flex justify-end mt-6">
      <AppButton color="green" :label="saveLabel" :icon="true">
        <template v-slot:icon>
          <Icon iconType="save" class="mr-2"/>
        </template>
      </AppButton>
    </div>
  </form>
</template>

<script>
import ContactAutocomplete from "@/components/AutoComplete/ContactAutoComplete.vue";
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import Spinner from "@/components/Spinner.vue";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  name: "ServiceTicketForm",
  mixins: [notificationMixin],
  components: {
    ContactAutocomplete,
    AppButton,
    Icon,
    Spinner,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    providedContact: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    saveLabel: {
      type: String,
      default: "Save",
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ticket: this.buildTicket(),
      selectedContact: null,
      customerFocused: false,
      customersEquipment: [],
      validationErrors: {},
      severityOptions: [
        {label: "Normal", value: 0},
        {label: "Low", value: 1},
        {label: "Medium", value: 2},
        {label: "High", value: 3},
      ],
    };
  },
  computed: {
    isTypedCustomer() {
      return !this.ticket.contact_id && !!String(this.ticket.contact_name || "").trim();
    },
    isContactLocked() {
      return this.isEdit;
    },
    isWarrantyLocked() {
      return this.isEdit;
    },
    createdByUser() {
      if (this.ticket.created_by) {
        return this.ticket.created_by;
      }
      const user = this.$store.state.user;
      if (user && (user.first_name || user.last_name)) {
        return `${user.first_name || ""} ${user.last_name || ""}`.trim();
      }
      return this.$store.state.userEmail || "";
    },
  },
  watch: {
    selectedContact(value) {
      if (value && typeof value === "object" && !value.contact_id && value.name) {
        this.applyTypedCustomer(value.name);
      }
    },
  },
  mounted() {
    if (this.providedContact) {
      this.applyContact(this.providedContact);
    } else if (this.ticket.contact_id || this.ticket.contact_name) {
      this.selectedContact = {
        contact_id: this.ticket.contact_id || null,
        name: this.ticket.contact_name,
      };
      this.getEquipment();
    }
  },
  methods: {
    buildTicket() {
      const source = this.value || {};
      return {
        contact_id: source.contact_id || "",
        contact_name: source.contact_name || "",
        equipment_id: source.equipment_id || "",
        title: source.title || "",
        severity: source.severity == null ? 0 : parseInt(source.severity),
        description: source.description || "",
        contact_name_additional: source.contact_name_additional || "",
        contact_number_additional: source.contact_number_additional || "",
        follow_up_at: source.follow_up_at
            ? this.$moment.unix(source.follow_up_at).toDate()
            : null,
        is_warranty: !!source.is_warranty,
        warranty_reason: source.warranty_reason || "",
        warranty_number: source.warranty_number || "",
        items_for_return: !!source.items_for_return,
        items_for_return_notes: source.items_for_return_notes || "",
        created_by: source.created_by || "",
      };
    },
    handleContactSelected(event) {
      this.applyContact(event.value || event);
    },
    handleContactCleared() {
      this.ticket.contact_id = "";
      this.ticket.contact_name = "";
      this.ticket.equipment_id = "";
      this.customersEquipment = [];
    },
    applyContact(contact) {
      this.selectedContact = contact;
      this.ticket.contact_id = contact.contact_id;
      this.ticket.contact_name = contact.name;
      this.getEquipment();
    },
    applyTypedCustomer(name) {
      this.ticket.contact_id = "";
      this.ticket.contact_name = String(name || "").trim();
      this.ticket.equipment_id = "";
      this.customersEquipment = [];
    },
    async getEquipment() {
      if (!this.ticket.contact_id) {
        this.customersEquipment = [];
        return;
      }
      try {
        const result = await this.ContactService.getEquipment(this.ticket.contact_id);
        this.customersEquipment = (result.data || []).map((item) => ({
          ...item,
          label: item.serial_number
              ? `${item.name} (SN:${item.serial_number})`
              : item.name,
        }));
      } catch (error) {
        console.error(error);
        this.customersEquipment = [];
      }
    },
    validate() {
      const errors = {};

      if (!this.ticket.contact_id && !String(this.ticket.contact_name || "").trim()) {
        errors.contact_id = "Please select or enter a customer.";
      }

      if (!String(this.ticket.contact_name_additional || "").trim()) {
        errors.contact_name_additional = "Please enter the contact name.";
      }

      if (!String(this.ticket.contact_number_additional || "").trim()) {
        errors.contact_number_additional = "Please enter the contact number.";
      }

      if (!this.ticket.title || !this.ticket.title.trim()) {
        errors.title = "Please enter a ticket title.";
      }

      if (
          !this.isEdit &&
          this.ticket.is_warranty &&
          !String(this.ticket.warranty_reason || "").trim()
      ) {
        errors.warranty_reason = "Please enter the reason warranty was agreed.";
      }

      if (
          this.ticket.items_for_return &&
          !String(this.ticket.items_for_return_notes || "").trim()
      ) {
        errors.items_for_return_notes = "Please enter the items for return.";
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    buildPayload() {
      const payload = {
        title: this.ticket.title.trim(),
        severity: parseInt(this.ticket.severity),
        description: this.ticket.description || "",
        contact_name_additional: String(this.ticket.contact_name_additional || "").trim(),
        contact_number_additional: String(this.ticket.contact_number_additional || "").trim(),
        items_for_return: !!this.ticket.items_for_return,
        items_for_return_notes: this.ticket.items_for_return
            ? this.ticket.items_for_return_notes
            : "",
      };

      if (!this.isEdit) {
        if (this.ticket.contact_id) {
          payload.contact_id = this.ticket.contact_id;
        } else {
          payload.contact_name = String(this.ticket.contact_name || "").trim();
        }
        payload.is_warranty = !!this.ticket.is_warranty;
        if (this.ticket.is_warranty) {
          payload.warranty_reason = this.ticket.warranty_reason;
        }
      }

      if (this.ticket.equipment_id) {
        payload.equipment_id = this.ticket.equipment_id;
      } else if (this.isEdit) {
        payload.equipment_id = "";
      }

      if (this.ticket.follow_up_at) {
        payload.follow_up_at = this.$moment(this.ticket.follow_up_at).unix();
      } else if (this.isEdit) {
        payload.follow_up_at = 0;
      }

      return payload;
    },
    async save() {
      if (!this.validate()) {
        this.notifyWarning("Please correct the highlighted fields.");
        return;
      }

      this.$emit("save", this.buildPayload());
    },
  },
};
</script>

<style scoped>

::v-deep .service-ticket-control.p-dropdown {
  display: flex;
  align-items: center;
  min-height: 2.625rem;
}

::v-deep .service-ticket-control .p-dropdown-label {
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
}

::v-deep .service-ticket-calendar .p-inputtext {
  width: 100%;
  min-height: 2.625rem;
  padding: 0.5rem 1rem;
  border: 1px solid #cbd5e0;
  border-right: none;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
  background-color: #f7fafc;
}

::v-deep .service-ticket-calendar .p-datepicker-trigger {
  min-height: 2.625rem;
  border: 1px solid #cbd5e0;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

::v-deep .service-ticket-calendar .p-inputtext:enabled:focus,
::v-deep .service-ticket-calendar .p-inputtext:enabled:hover {
  border-color: #ed8936;
  border-right: none;
}

</style>
